<script lang="ts">
import { Image } from '@builder.io/sdk-svelte';
import { getAuthorPageUrl } from '$lib/utils/authorSlug.js';

interface Props {
    author: {
        name: string;
        role?: string;
        bioImage: string;
        shortBio: string;
        linkedIn?: string;
    };
    authorPostCount?: number;
    hideBlock?: boolean;
}

const { author, authorPostCount, hideBlock = false }: Props = $props();

const authorPageUrl = $derived(getAuthorPageUrl(author.name));
const showMoreLink = $derived(
    authorPostCount !== undefined && authorPostCount > 1 && authorPageUrl,
);
</script>

{#if !hideBlock}
<div class="author-bio">
	<div class="author-bio__container">
		<div class="author-bio__image">
			<Image image={author.bioImage} altText={author.name} />
		</div>
		<div class="author-bio__content">
			<h3 class="author-bio__heading">About the Author</h3>
			<div class="author-bio__details">
				<span class="author-bio__name">{author.name}</span>
				{#if author.role}
					<span class="author-bio__role">{author.role}</span>
				{/if}
			</div>
			<p class="author-bio__text">{author.shortBio}</p>
			{#if showMoreLink || author.linkedIn}
				<div class="author-bio__links">
					{#if showMoreLink}
						<a href={authorPageUrl} class="author-bio__more-link">
							{author.name}'s Thoughts
						</a>
					{/if}
					{#if author.linkedIn}
						<a
							href={author.linkedIn}
							target="_blank"
							rel="noopener noreferrer"
							class="author-bio__linkedin"
							aria-label="{author.name} on LinkedIn (opens in new tab)"
						>
							<svg
								class="author-bio__linkedin-icon"
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="currentColor"
								aria-hidden="true"
							>
								<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
							</svg>
							View profile
						</a>
					{/if}
				</div>
			{/if}
		</div>
	</div>
</div>
{/if}

<style lang="scss">
	@use '/src/scss/global-constants/typography';
	@use '/src/scss/mixins/builder-rich-text' as builder-mixins;
	@use 'sass-mq/mq' as *;

	.author-bio {
		margin: 0 auto;
		max-width: 93ch;
		padding: 0 var(--layout-pagegrid-margin);

		&__container {
			border-top: 1px solid var(--colour-noghrei-silver);
			display: grid;
			align-items: start;
			gap: 0 2rem;
			grid-template-columns: 10rem 1fr;
			padding-top: 4rem;

			@include mq($from: tablet) {
				gap: 4rem;
				grid-template-columns: 25rem 1fr;
				padding-top: 5.6rem;
			}
		}

		&__content {
			@include mq($until: tablet) {
				display: contents;
			}
		}

		&__details {
			@include mq($until: tablet) {
				display: contents;
			}
		}

		&__image {
			width: 10rem;

			@include mq($until: tablet) {
				grid-row: 1 / 4;
			}

			@include mq($from: tablet) {
				width: 100%;
			}

			:global(img) {
				display: block;
				height: auto;
				width: 100%;
			}
		}

		&__heading {
			color: var(--colour-squant);
			font-size: 1.2rem;
			font-weight: 500;
			letter-spacing: 0.1em;
			text-transform: uppercase;

			@include mq($until: tablet) {
				margin-bottom: 0.4rem;
			}

			@include mq($from: tablet) {
				font-size: 1.6rem;
				margin-bottom: 2rem;
			}
		}

		&__details {
			@include mq($from: tablet) {
				margin-bottom: 1.6rem;
			}
		}

		&__name {
			color: var(--colour-dreamless-sleep);
			display: block;
			@include typography.label-name;

			@include mq($until: tablet) {
				margin-bottom: 0.2rem;
			}

			@include mq($from: tablet) {
				margin-bottom: 0.5rem;
			}
		}

		&__role {
			color: var(--colour-squant);
			display: block;
			text-transform: uppercase;
			@include typography.label-meta;
		}

		&__text {
			color: var(--colour-dreamless-sleep);
			@include typography.body-base;

			@include mq($until: tablet) {
				grid-column: 1 / -1;
				grid-row: 4;
				margin-top: 1.6rem;
			}

			@include mq($from: tablet) {
				margin-top: 1.6rem;
			}
		}

		&__links {
			display: flex;
			flex-wrap: wrap;
			gap: 1.6rem;
			margin-top: 1.6rem;

			@include mq($until: tablet) {
				grid-column: 1 / -1;
				grid-row: 5;
			}
		}

		&__more-link,
		&__linkedin {
			@include builder-mixins.rich-text-link-styles;
			display: inline-flex;
			align-items: center;
			font-weight: 500;
			@include typography.body-base;
		}

		&__linkedin {
			gap: 0.4rem;
		}

		&__linkedin-icon {
			flex-shrink: 0;
		}
	}
</style>
